import React from 'react';

import { TGridPagination } from '@private/data-grid';
import { useUpdateEffect } from '@private/hooks';

import ItemsPerPage from 'components/Grids/DataGrid/ItemsPerPage';
import {
  Button,
  Column,
  Container,
  Ellipsis,
  Pagination,
  PaginationInfo,
} from 'components/PaginationToolbar/styled';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useBreakpoint } from 'hooks/adaptive/useBreakpoint';

interface IBottomToolbarProps {
  disabled?: boolean;
  hidePerPage?: boolean;
  pagination: {
    pageNavigation: TGridPagination['pageNavigation'];
    perPageSelect?: TGridPagination['perPageSelect'];
  };
}

const PaginationToolbar = ({
  disabled = false,
  hidePerPage,
  pagination,
}: IBottomToolbarProps) => {
  const checkWindowWidth = useBreakpoint();

  const {
    pageNavigation: { currentPage, onChange, perPage, textConfig, total },
    perPageSelect: { onChange: perPageChange } = {},
  } = pagination;
  const totalPages = Math.ceil(total / perPage);

  const visiblePerPage = checkWindowWidth(1000) ? 3 : 4;

  const createPagesArray = () => {
    const range = { start: 1, stop: totalPages };

    if (totalPages <= visiblePerPage + 1) {
      range.start = 2;
      range.stop = totalPages - 1;
    } else if (currentPage < visiblePerPage - 1) {
      range.start = 2;
      range.stop = visiblePerPage;
    } else if (
      currentPage >= visiblePerPage - 1 &&
      currentPage <= totalPages - visiblePerPage
    ) {
      range.start = currentPage;
      range.stop = currentPage + 2;
    } else if (currentPage > totalPages - visiblePerPage) {
      range.start = totalPages - (visiblePerPage - 1);
      range.stop = totalPages - 1;
    }

    const result = [
      1,
      ...Array.from(
        { length: range.stop - range.start + 1 },
        (_value, index) => range.start + index,
      ),
    ];

    if (totalPages !== 1 && totalPages !== 0) result.push(totalPages);

    return result;
  };

  const [pages, setPages] = React.useState<number[]>(createPagesArray);

  useUpdateEffect(() => {
    setPages(createPagesArray());
  }, [currentPage, total, perPage]);

  const onChangePage = (newPage: number) => () => {
    if (onChange && newPage > 0 && newPage <= totalPages && !disabled) {
      onChange(newPage - 1);
    }
  };

  const getPaginationText = React.useMemo(() => {
    const pageFrom = total ? currentPage * perPage + 1 : 0;
    const pageTo = currentPage * perPage + perPage;

    return `${pageFrom} ${textConfig?.to} ${pageTo >= total ? total : pageTo} ${
      textConfig?.of
    } ${total} ${textConfig?.entries}`;
  }, [total, currentPage, perPage, textConfig?.to, textConfig?.of, textConfig?.entries]);

  return (
    <Container $isHidden={!total}>
      <PaginationInfo size='sm' testIds={{ root: 'totalElements' }}>
        {getPaginationText}
      </PaginationInfo>
      <Column>
        {!hidePerPage && <ItemsPerPage onChange={perPageChange} value={perPage} />}
        <Pagination>
          <Button
            $disabled={currentPage === 0 || disabled}
            data-testid='previousPageButton'
            onClick={onChangePage(currentPage)}
          >
            <ICONS_MAP.MinimalLeftArrow width={7} />
          </Button>
          {pages?.map((value, index) => {
            if (index === 0 || pages[index] - pages[index - 1] === 1) {
              return (
                <Button
                  $disabled={disabled}
                  $isActive={currentPage + 1 === value}
                  data-testid={`pageButton-${value}`}
                  key={index}
                  onClick={onChangePage(value)}
                >
                  {value}
                </Button>
              );
            }
            return (
              <React.Fragment key={index}>
                <Ellipsis data-testid='ellipsis'>...</Ellipsis>
                <Button
                  $disabled={disabled}
                  $isActive={currentPage + 1 === value}
                  data-testid={`pageButton-${value}`}
                  onClick={onChangePage(value)}
                >
                  {value}
                </Button>
              </React.Fragment>
            );
          })}
          <Button
            $disabled={currentPage + 1 === totalPages || totalPages === 0 || disabled}
            data-testid='nextPageButton'
            onClick={onChangePage(currentPage + 2)}
          >
            <ICONS_MAP.MinimalRightArrow width={7} />
          </Button>
        </Pagination>
      </Column>
    </Container>
  );
};

export default appReactMemo(PaginationToolbar);
